/**
 * "ResizeObserver loop completed with undelivered notifications" (and the older
 * "ResizeObserver loop limit exceeded") is a benign browser notification defined
 * by the ResizeObserver spec: it fires when observer callbacks cause further
 * layout changes that spill into the next delivery cycle. It has no user-facing
 * effect and is commonly emitted by third-party components that reposition on
 * resize (e.g. Mantine's floating dropdowns).
 *
 * It is harmless, but webpack-dev-server's error overlay and Sentry both listen
 * to `window.onerror` and surface it as a runtime error. This installs an early
 * capture-phase listener that swallows only this specific message, leaving every
 * other error untouched.
 */
const RESIZE_OBSERVER_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
];

const isResizeObserverError = (message: string | undefined): boolean =>
  typeof message === 'string' &&
  RESIZE_OBSERVER_MESSAGES.some((candidate) => message.includes(candidate));

export const suppressResizeObserverLoopError = (): void => {
  if (typeof window === 'undefined') return;

  window.addEventListener(
    'error',
    (event) => {
      if (isResizeObserverError(event.message)) {
        // Capture phase + stopImmediatePropagation prevents the dev-server
        // overlay's (bubble-phase) handler from ever seeing this event.
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true
  );
};
